<template>
  <v-container id="qr-view" class="fill-height" tag="section">
    <v-row justify="center">
      <v-col cols="12">
        <v-slide-y-transition appear>

          <v-card light max-width="950" rounded
            class="mx-auto provisioning-panel section-card section-card--qr">
            <v-card-title class="section-card__title">
              <div>
                <div class="text-subtitle-1 font-weight-medium">
                  Provisioning QR Code
                </div>
                <div class="text-caption grey--text text--darken-1">
                  Scan this QR during Android Enterprise enrollment.
                </div>
              </div>
            </v-card-title>
            <v-card-text class="section-card__body section-card__body--qr">
              <v-row>
                <!-- Configuration & WiFi Settings Form -->
                <v-col cols="12" md="6" class="pr-md-4">
                  <!-- Dummy hidden input elements to capture browser autocomplete/autofill -->
                  <input type="text" name="dummy_username" style="position: absolute; top: -100px; left: -100px; width: 1px; height: 1px; opacity: 0; pointer-events: none;" tabindex="-1" />
                  <input type="password" name="dummy_password" autocomplete="new-password" style="position: absolute; top: -100px; left: -100px; width: 1px; height: 1px; opacity: 0; pointer-events: none;" tabindex="-1" />

                  <div class="text-subtitle-2 font-weight-bold my-4 primary--text d-flex align-center">
                    <v-icon small class="mr-2" color="primary">mdi-wifi</v-icon>
                    Wi-Fi Settings
                  </div>

                  <v-text-field
                    v-model="wifiSsid"
                    outlined
                    dense
                    label="WiFi SSID"
                    placeholder="Enter WiFi SSID"
                    hint="The SSID (name) of the Wi-Fi network"
                    persistent-hint
                    browser-autocomplete="new-password"
                    class="mb-4"
                  />

                  <v-autocomplete
                    v-model="wifiSecurityType"
                    outlined
                    dense
                    label="WiFi Security Type"
                    :items="securityTypes"
                    hint="Security protocol used by the network"
                    persistent-hint
                    browser-autocomplete="new-password"
                    class="mb-4"
                  />

                  <v-text-field
                    v-model="wifiPassword"
                    outlined
                    dense
                    label="WiFi Password"
                    placeholder="Enter WiFi Password"
                    :type="showWifiPassword ? 'text' : 'password'"
                    :append-icon="showWifiPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    @click:append="showWifiPassword = !showWifiPassword"
                    hint="Wi-Fi network password"
                    persistent-hint
                    browser-autocomplete="new-password"
                    class="mb-4"
                  />
                </v-col>

                <!-- QR Preview and Actions -->
                <v-col cols="12" md="6" class="d-flex flex-column justify-space-between pl-md-4">
                  <div class="text-subtitle-2 font-weight-bold mb-4 primary--text d-flex align-center">
                    <v-icon small class="mr-2" color="primary">mdi-qrcode-scan</v-icon>
                    QR Code Preview
                  </div>

                  <div ref="qrCanvasWrapper" class="qr-preview" :class="{ 'qr-preview--empty': !qrData }">
                    <div v-if="qrData" class="qr-preview__frame">
                      <qrcode-vue :value="qrData" :size="$vuetify.breakpoint.mdAndDown ? 300 : 360" level="L"
                        render-as="canvas" />
                      <div class="qr-label">{{ vehicleNo }}</div>
                    </div>

                    <div v-else class="text-body-2 text-center grey--text text--darken-1">
                      <div v-if="error">{{ error }}</div>
                      <div v-else>
                        <v-progress-circular indeterminate color="primary" :size="30" class="mr-2"></v-progress-circular>
                        Generating Qr {{ vehicleNo }} ...
                      </div>
                    </div>
                  </div>

                  <div class="qr-actions">
                    <app-btn class="qr-action" :disabled="!qrData" @click="copyQrJson()">
                      <v-icon class="mr-1">
                        mdi-content-copy
                      </v-icon>
                      Copy JSON
                    </app-btn>

                    <app-btn class="qr-action" :disabled="!qrData" @click="downloadQrCode()">
                      <v-icon class="mr-1">
                        mdi-download
                      </v-icon>
                      Download QR Code
                    </app-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-slide-y-transition>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import QrcodeVue from 'qrcode.vue'

export default {
  name: 'QrView',
  components: { QrcodeVue },
  data: () => ({
    vehicleNo: '',
    qrObject: null,
    error: '',
    wifiSsid: '',
    wifiPassword: '',
    wifiSecurityType: 'NONE',
    showWifiPassword: false,
    securityTypes: [
      'NONE',
      'WEP',
      'WPA',
      'WPA2',
      'WPA3',
      'WPA/WPA2 PSK',
      'WPA2/WPA3 PSK',
      'SAE',
      'OWE',
      'EAP',
      'EAP-TLS',
      'EAP-TTLS',
      'PEAP'
    ]
  }),
  computed: {
    qrData() {
      if (!this.qrObject) return ''
      const updatedObj = {
        ...this.qrObject,
        'android.app.extra.PROVISIONING_WIFI_SSID': this.wifiSsid,
        'android.app.extra.PROVISIONING_WIFI_PASSWORD': this.wifiPassword,
        'android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE': this.wifiSecurityType
      }
      return JSON.stringify(updatedObj)
    }
  },
  created() {
    const id = this.$route.params.id;
    this.getQrData(id)
  },
  methods: {
    async getQrData(id) {
      this.$http(`/QrProvisioning/Qr/${id}`).then(res=>{
        if(res.data.success && res.data.data) {
          const qrObj = res.data.data
          this.qrObject = qrObj
          this.wifiSsid = qrObj['android.app.extra.PROVISIONING_WIFI_SSID'] || ''
          this.wifiPassword = qrObj['android.app.extra.PROVISIONING_WIFI_PASSWORD'] || ''
          this.wifiSecurityType = qrObj['android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE'] || 'NONE'

          let vehicleNumber = ''
          const extras = qrObj['android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE']
          if (extras) {
            if (typeof extras === 'object') {
              vehicleNumber = extras.vehicleNumber || ''
            } else if (typeof extras === 'string') {
              try {
                const parsedExtras = JSON.parse(extras)
                vehicleNumber = parsedExtras.vehicleNumber || ''
              } catch (e) {
                // Not JSON or parse failed
              }
            }
          }
          this.vehicleNo = vehicleNumber || res.data.vehicleNo || res.data.deviceName || ''
        }else {
          this.error = res.data.message
        }
      }).catch(()=> {
        this.error = 'Something went wrong while fetching the Qr'
      })
    },

     async copyQrJson() {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.qrData)
        }
        this.$toast.success(
          'Provisioning JSON copied to the clipboard.',
          'Success',
          { position: 'topRight' },
        )
      } catch (error) {
        this.$toast.error(
          'We could not copy the JSON automatically.',
          'Error',
          { position: 'topRight' },
        )
      }
    },

    async downloadQrCode() {
      await this.$nextTick();

      const qrCanvas = this.$refs.qrCanvasWrapper?.querySelector('canvas')

      if (!qrCanvas) {
        this.$toast.error(
          'The QR code is not ready to download yet.',
          'Error',
          { position: 'topRight' },
        )
        return
      }

      const downloadLink = document.createElement('a')
      downloadLink.href = qrCanvas.toDataURL('image/png')
      downloadLink.download = this.vehicleNo + '-provisioning-qr.png'
      downloadLink.click()
    }
  },
}
</script>

<style>
.provisioning-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-card {
  border-color: rgba(0, 0, 0, 0.08) !important;
  border-radius: 16px;
}

.section-card__title {
  padding: 18px 22px 14px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.section-card__title-content {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.section-card__title-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
  gap: 10px;
  flex-wrap: wrap;
}

.section-card__body {
  padding: 20px 22px 22px !important;
}

.section-upload-btn {
  min-width: 220px;
  min-height: 48px;
  justify-content: center;
  white-space: nowrap;
}

.section-select-btn {
  min-width: 172px;
  min-height: 48px !important;
  white-space: nowrap;
}

.wifi-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.section-card__body--qr {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.provisioning-note,
.qr-warning {
  border-radius: 12px;
}

.qr-warning {
  margin-bottom: 12px !important;
}

.qr-warning ::v-deep .v-alert__wrapper {
  align-items: center;
}

.qr-warning ::v-deep .v-alert__content {
  font-size: 0.95rem;
  line-height: 1.35;
}

.qr-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  min-height: 520px;
  padding: 20px;
  border-radius: 8px;
  background: #fafafa;
}

.qr-preview--empty {
  border: 1px dashed rgba(0, 0, 0, 0.12);
}

.qr-preview ::v-deep canvas {
  display: block;
  max-width: 100%;
  height: auto !important;
}

.qr-preview__frame {
  padding: 28px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
}

.qr-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 20px;
}

.qr-action {
  width: 100%;
  margin: 0 !important;
  justify-content: center;
}

@media (max-width: 1263px) {
  .section-card--qr {
    margin-top: 4px;
  }
}

@media (max-width: 960px) {

  /* Trim verbose descriptions that eat vertical space on phones. */
  .provisioning-note {
    display: none !important;
  }

  .section-card__subtext {
    display: none;
  }

  .kiosk-shell ::v-deep .v-card--material__title .text-subtitle-1 {
    display: none;
  }

  .section-card__title {
    padding: 16px 18px 12px !important;
  }

  .section-card__title-content {
    align-items: stretch;
    flex-direction: column;
  }

  .section-card__title-actions {
    width: 100%;
    justify-content: stretch;
    margin-left: 0;
    flex-direction: column;
  }

  .section-card__body {
    padding: 16px 18px 18px !important;
  }

  .wifi-section-heading {
    align-items: flex-start;
  }

  .section-upload-btn {
    width: 100%;
  }

  .section-select-btn {
    width: 100%;
  }

  .qr-preview {
    min-height: 360px;
    padding: 16px;
  }

  .qr-preview__frame {
    padding: 20px;
  }

  .qr-actions {
    grid-template-columns: 1fr;
  }


}

.qr-label {
  font-size: 18px;
  font-weight: 500;
  color: black;
  text-align: center;
  margin-top: 8px;
}
</style>
